import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ApiService } from '@dav-core/services/api.service';
import { AuthService } from '@dav-core/services/auth.service';
import { ConfigService } from '@dav-core/services/config.service';
import { ModalService } from '@dav-core/services/modal.service';
import { LazyOktaService } from '@dav-core/services/okta/lazy-okta.service';
import { PendingContractsWarningService } from '@dav-core/services/pendingContractsWarning.service';
import { DealerInfoResponse } from '@dav-model/api/response/dealer-info.response.model';
import { LoadQuote } from '@dav-model/cart/load-quote.model';
import { OktaLoginRequest } from '@dav-model/login/request/okta-login.request';
import { AllDealers, UserResponse } from '@dav-model/login/response/user.response.model';
import { GetUserResponse } from '@dav-model/response/getuser.response';
import { UserRole } from '@dav-model/user-role.enum';
import * as fromRoot from '@dav-store/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as UserActions from './user.actions';
import { LogInSuccessAction, UpdatePendingNumberAction } from './user.actions';
import { selectOriginalUser, selectUser } from './user.selectors';
import { environment } from '../../../environments/environment';
import * as CartActions from '../cart/cart.actions';
import { State } from '../config/config.reducer';
import * as ProductActions from '../product/product.actions';
import * as VehicleActions from '../vehicle/vehicle.actions';
import { AccessScope } from '@dav-model/login/access-scopes.model';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private configService: ConfigService,
    private router: Router,
    private authService: AuthService,
    private store$: Store<fromRoot.State>,
    private modalService: ModalService,
    private warningService: PendingContractsWarningService,
    private oktaService: LazyOktaService
  ) {}

  login$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.LOGIN),
      withLatestFrom(this.store$.select((state) => state.configState)),
      switchMap(([action, configState]: [UserActions.LogInAction, State]) => {
        const req = {
          username: action.payload.username,
          password: action.payload.password,
          application: environment.application,
          programId: configState.clientConfig.programId,
          captcha: action.payload.captcha,
        };

        return this.apiService.login(req).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            this.configService.hideNewVersionMessage();
            // verify - update password page
            if (response.isTempPassword === 'Y') {
              return {
                type: UserActions.UPDATE_PASSWORD,
                payload: { response: response, admin: action.payload.admin },
              };
            } else {
              return {
                type: UserActions.LOGIN_SUCCESS,
                payload: { response: response, admin: action.payload.admin },
              };
            }
          }),
          // If request fails, dispatch failed action
          catchError((error) =>
            of({
              type: UserActions.LOGIN_FAILURE,
              payload: error,
            })
          )
        );
      })
    )
  );

  ssoOktaLogin$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.SSO_OKTA_LOGIN),
      switchMap((action: { type: string; payload: { oktaLoginRequest: OktaLoginRequest } }) =>
        this.apiService.getOktaUser(action.payload.oktaLoginRequest).pipe(
          map((response: UserResponse) => ({ type: UserActions.LOGIN_SUCCESS, payload: { response, admin: false } })),
          catchError((error) => of({ type: UserActions.SSO_OKTA_LOGIN_FAILURE, payload: error }))
        )
      )
    )
  );

  ssoOktaLoginFailure$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.SSO_OKTA_LOGIN_FAILURE),
        tap(() => this.router.navigate(['/callback/error']))
      ),
    { dispatch: false }
  );

  LogInSuccess$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.LOGIN_SUCCESS),
        tap((action: LogInSuccessAction) => {
          // previously used to also set token in localStorage
          // but now used to just navigate if the login was successful
          this.authService.resetTokenRefreshTimestamp();

          sessionStorage.setItem('token', action.payload.response.token);
          const userState = sessionStorage.getItem('userState');
          if (userState) {
            const userStateObject = JSON.parse(userState);
            if (userStateObject.previousUser !== action.payload.response.userName) {
              this.store$.dispatch(new VehicleActions.ResetProductFormAction());
              this.store$.dispatch(new ProductActions.ResetProductsAction());
              this.store$.dispatch(new CartActions.ResetCartAction());
            }
          }
          if (action.payload.admin) {
            this.router.navigate(['/admin/console']);
          } else {
            this.router.navigate(['/']);
          }

          if (action.payload.response.pendingContracts) {
            this.store$.dispatch(new UpdatePendingNumberAction(action.payload.response.pendingContracts));
          }
        })
      ),
    { dispatch: false }
  );

  ValidateOktaPasswordResetToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.SSO_OKTA_VALIDATE_PASSWORD_RESET_TOKEN),
        switchMap((action: UserActions.SsoValidateOktaPasswordResetTokenreAction) =>
          this.apiService.validatePasswordResetToken(action.payload.token)
        ),
        tap(({ stateToken, username }) => {
          this.modalService.openupdatePasswordModal({
            stateToken,
            username,
            flowType: 'OKTA',
            actionType: 'RESET',
            flag: true,
          });
        })
      ),
    { dispatch: false }
  );

  UpdatePassword$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.UPDATE_PASSWORD),
        tap((action: UserActions.UpdatePasswordAction) => {
          // trigger popup for update password
          sessionStorage.setItem('token', action.payload.response.token);
          this.modalService.openupdatePasswordModal({
            username: action.payload.response.userName,
            actionType: 'UPDATE',
            flowType: 'LEGACY',
            flag: true,
          });
        })
      ),
    { dispatch: false }
  );

  LogOut$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.LOGOUT),
      withLatestFrom(
        this.store$.select((state) => state.userState.user),
        this.store$.select((state) => state.userState.emulatedDealer)
      ),
      switchMap(([action, user, emulatedDealer]: [UserActions.LogOutAction, UserResponse, boolean]) => {
        if (action.payload.newVersion) {
          this.store$.dispatch(new UserActions.ResetSessionAction());
          this.store$.dispatch(new UserActions.UpdateVersionAction(action.payload.version));
          this.configService.displayNewVersionMessage();
        }

        if (user && user.userName) {
          this.store$.dispatch(new UserActions.UpdatePreviousUserAction(user.userName));
        }

        if (emulatedDealer) {
          this.store$.dispatch(new UserActions.ResetSessionAction());
        }
        this.authService.cancelSessionTimer();
        this.authService.cancelTokenRenewal();
        this.modalService.closeAllModals();
        sessionStorage.removeItem('token');
        this.store$.dispatch(new UserActions.ResetSessionAction());

        if (environment.ssoFlow) {
          this.oktaService.oktaSignOut();
        } else {
          this.router.navigate(['/login']);
        }

        return of({
          type: UserActions.LOGOUT_SUCCESS,
        });
      })
    )
  );

  ResetSession$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.RESET_SESSION),
        tap(() => {
          this.configService.hideErrorMessage();
          this.store$.dispatch(new VehicleActions.ResetProductFormAction());
          this.store$.dispatch(new ProductActions.ResetProductsAction());
          this.store$.dispatch(new CartActions.ResetCartAction());
        })
      ),
    { dispatch: false }
  );

  emulateDealer$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.EMULATE_DEALER),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([action, user]: [UserActions.EmulateDealerAction, UserResponse]) => {
          return this._prepareUser(user, action.dealer).pipe(
            tap(
              (userInfo: {
                dealer: DealerInfoResponse;
                associatedDealers: AllDealers[];
                relatedApplicationId: string;
                complianceId;
                stateId;
                compliance;
              }) => {
                sessionStorage.setItem('token', userInfo.dealer.token);
                const updateUser = JSON.parse(JSON.stringify(user)) as UserResponse;

                updateUser.cmsDealerNumber = userInfo.dealer.cmsDealerNumber;
                updateUser.dealerInfo.dealerId = userInfo.dealer.dealerId;
                updateUser.dealerInfo.dealerName = userInfo.dealer.dealerName;
                updateUser.dealerInfo.dealerCity = userInfo.dealer.dealerCity;
                updateUser.dealerInfo.dealerFullName = userInfo.dealer.dealerFullName;
                updateUser.dealerInfo.dealerPhone = userInfo.dealer.dealerPhone;
                updateUser.dealerInfo.dealerAddress1 = userInfo.dealer.dealerAddress1;

                updateUser.dealerInfo.dealerState = userInfo.dealer.dealerState;
                updateUser.dealerInfo.stateId = userInfo.dealer.stateId;
                updateUser.compliance = userInfo.dealer.compliance;
                updateUser.name = userInfo.dealer.dealerName;
                updateUser.allDealers = userInfo.associatedDealers;

                const originalUser = JSON.parse(localStorage.getItem('userState'));
                if (
                  originalUser.originalUser &&
                  originalUser.originalUser.allDealers &&
                  originalUser.originalUser.allDealers[0].dealerBrandRestrictions
                ) {
                  updateUser.allDealers.forEach((dealer) => {
                    dealer['dealerBrandRestrictions'] = originalUser.originalUser.allDealers[0].dealerBrandRestrictions;
                  });
                }

                // updating user property
                updateUser.complianceId = userInfo.complianceId;
                updateUser.compliance = userInfo.compliance;
                updateUser.dealerInfo.stateId = userInfo.stateId;
                updateUser.relatedApplicationId = userInfo.relatedApplicationId;

                this.store$.dispatch(new UserActions.GetRecentQuotesAction());
                this.store$.dispatch(new UserActions.EmulateDealerSuccessAction(updateUser));
                this.store$.dispatch(new UserActions.ResetSessionAction());

                this.router.navigate(['/home']);
                this.warningService.showToastrs();
                this.warningService.showModal();
              }
            )
          );
        })
      ),
    { dispatch: false }
  );

  resetDealer$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.RESET_DEALER),
        withLatestFrom(this.store$.select(selectUser), this.store$.select(selectOriginalUser)),
        switchMap(([, originalUser]) =>
          this._getDealerInfo(originalUser, originalUser.allDealers[0].cmsDealerNumber, true).pipe(
            tap((dealers: DealerInfoResponse[]) => {
              if (dealers?.length) {
                sessionStorage.setItem('token', dealers[0].token);
                this.store$.dispatch(new UserActions.GetRecentQuotesAction());
                this.store$.dispatch(new ProductActions.ResetProductsAction());
                this.store$.dispatch(new VehicleActions.ResetProductFormAction());
                this.store$.dispatch(new CartActions.ResetCartAction());
                this.router.navigate(['/home']);
                this.warningService.showToastrs();
                this.warningService.showModal();
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  UpdateVehicleInfo$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.UPDATE_VEHICLE_INFO),
        tap(() => {
          this.configService.hideErrorMessage();
          this.store$.dispatch(new ProductActions.ResetProductsAction());
          this.store$.dispatch(new CartActions.ResetCartAction());
        })
      ),
    { dispatch: false }
  );

  SaveMarkupToTopFilter$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.SAVE_MARKUP),
        withLatestFrom(this.store$.select((state) => state.userState.user)),
        tap(([action]: [UserActions.SaveMarkupAction, UserResponse]) => {
          this.store$.dispatch(new ProductActions.ChangeMarkupAction(action.payload));
        })
      ),
    { dispatch: false }
  );

  saveMarkupApiCall$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.SAVE_MARKUP),
        withLatestFrom(this.store$.select((state) => state.userState.user)),
        switchMap(([action, user]: [UserActions.SaveMarkupAction, UserResponse]) =>
          this.apiService.saveMarkup(action.payload, user.userName)
        )
      ),
    { dispatch: false }
  );

  ssoLoginFailure$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.SSO_LOGIN_FAILURE),
        tap(() => this.router.navigate(['/callback/error']))
      ),
    { dispatch: false }
  );

  getRecentQuotes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.GET_RECENT_QUOTES),
      withLatestFrom(this.store$.select((state) => state.userState.user)),
      switchMap(([, user]: [UserActions.GetRecentQuotesAction, UserResponse]) => {
        return this.apiService.getRecentQuotes(user.cmsDealerNumber).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            return {
              type: UserActions.GET_RECENT_QUOTES_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) =>
            of({
              type: UserActions.GET_RECENT_QUOTES_SUCCESS,
              payload: error,
            })
          )
        );
      })
    )
  );

  getQuote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.GET_QUOTE),
      withLatestFrom(this.store$.select((store) => store.userState.user)),
      switchMap(([action, user]: [UserActions.GetQuoteAction, UserResponse]) => {
        const request = {
          dealer: action.payload.dealer,
          vin: action.payload.vin,
        };

        return this.callLoadQuote(request, user).pipe(
          // If successful, dispatch success action with result
          map((response: LoadQuote) => {
            return {
              type: CartActions.LOAD_QUOTE_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError(() =>
            of({
              type: UserActions.LOGIN_FAILURE,
              payload: {},
            })
          )
        );
      })
    )
  );

  deleteQuote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.DELETE_QUOTE),
      switchMap((action: UserActions.DeleteQuoteAction) => {
        const request = {
          dealer: action.payload.dealer,
          vin: action.payload.vin,
        };

        return this.apiService.deleteQuote(request).pipe(
          // If successful, dispatch success action with result
          map((response: any) => {
            return {
              type: UserActions.DELETE_QUOTE_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError(() =>
            of({
              type: UserActions.LOGIN_FAILURE,
              payload: {},
            })
          )
        );
      })
    )
  );

  ssoLogin$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.SSO_LOGIN),
      switchMap(() => {
        return this.apiService.ssoLogin({ application: environment.application }).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            return {
              type: UserActions.LOGIN_SUCCESS,
              payload: { response: response, admin: false },
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) => {
            console.error(error);

            return of({
              type: UserActions.SSO_LOGIN_FAILURE,
              payload: error,
            });
          })
        );
      })
    )
  );

  // This is a duplicate of callLoadQuote in Cart
  // duplication was required because of the dependency created
  // in cart.effects
  // will need to refactor
  // TODO
  callLoadQuote(request: any, user: UserResponse) {
    if (user && user.isRelatedDealer) {
      return this.apiService.loadQuoteOnly(request);
    }

    return this.apiService.loadQuote(request);
  }

  private _getDealerInfo(
    user: UserResponse,
    dealer: string,
    getFullInfo: boolean = false
  ): Observable<DealerInfoResponse[]> {
    const dealerInfo = this.apiService.getDealerInfo({
      actualDealerGroup: user.actualDealerGroup,
      useDealerGroup: user.useDealerGroup,
      application: environment.application,
      programId: user.programId,
      dealerId: user.dealerInfo.dealerId,
      userName: user.userName,
      dealer,
      dealerEmulateAll: Boolean(user.scopes.find((scope: AccessScope) => scope === 'DEALER_EMULATE_ALL')),
      getFullInfo,
    });

    return dealerInfo;
  }

  private _prepareUser(
    user: UserResponse,
    dealer: DealerInfoResponse
  ): Observable<{
    dealer: DealerInfoResponse;
    associatedDealers: AllDealers[];
    relatedApplicationId: string;
    complianceId;
    stateId;
    compliance;
  }> {
    const userInfo: {
      dealer: DealerInfoResponse;
      associatedDealers: AllDealers[];
      relatedApplicationId: string;
      complianceId;
      stateId;
      compliance;
    } = {
      dealer,
      associatedDealers: [],
      relatedApplicationId: '',
      stateId: user.dealerInfo.stateId,
      complianceId: user.complianceId,
      compliance: user.compliance,
    };

    return this._getDealerInfo(user, dealer.cmsDealerNumber, true).pipe(
      map((dealers: DealerInfoResponse[]) => ({
        ...userInfo,
        dealer: dealers.length ? { ...userInfo.dealer, ...dealers[0] } : userInfo.dealer,
      })),
      switchMap((userInfo) =>
        this.apiService
          .getassociateDealers({
            application: environment.application,
            programId: user.programId,
            cmsDealerNumber: dealer.cmsDealerNumber,
          })
          .pipe(
            map((data) => {
              if (data && data.allDealers) {
                userInfo.associatedDealers = data.allDealers;

                for (const dealer of data.allDealers) {
                  if (dealer.primary === false) {
                    userInfo.relatedApplicationId = dealer.applicationId;
                  }
                }

                userInfo.complianceId = data.complianceId;
                userInfo.stateId = data.stateId;
                userInfo.compliance = data.compliance;
              }

              return userInfo;
            })
          )
      )
    );
  }
}
